<template>
  <EmptyModal :open="open" width="480px" classes="!bg-white" @update:open="$emit('update:open', $event)">
    <div class="flex flex-col p-4">
      <h2 class="mb-6 text-center text-2xl font-bold text-txprimary">Сохранить в подборку</h2>
      <p class="mb-2 text-sm text-txprimary/60">Выбрать подборку</p>

      <div v-if="isLoading" class="flex justify-center py-10">
        <div class="size-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>

      <div
        v-else-if="collections.length"
        class="collections-list max-h-[264px] overflow-y-auto rounded-xl border border-[#D9E5FF]"
      >
        <button
          v-for="collection in collections"
          :key="collection.id"
          type="button"
          class="group flex w-full items-center justify-between gap-3 border-b border-[#D9E5FF] px-4 py-3 text-left text-sm text-txprimary last:border-b-0 hover:bg-bgnew"
          :disabled="savingId === collection.id"
          @click="save(collection)"
        >
          <span class="truncate">{{ collection.name }}</span>
          <span class="flex size-5 shrink-0 items-center justify-center">
            <Icon
              v-if="savedIds.has(collection.id)"
              name="lucide:check-circle-2"
              class="size-5 text-[#2DCD80]"
            />
            <Icon
              v-else
              name="lucide:plus"
              class="size-5 text-txprimary/40 opacity-0 transition-opacity group-hover:opacity-100"
            />
          </span>
        </button>
      </div>

      <p v-else class="rounded-xl border border-[#D9E5FF] px-4 py-3 text-sm text-txopacity">
        Подборок пока нет — создайте первую
      </p>

      <div v-if="isCreating" class="mt-3 flex items-center gap-2">
        <input
          v-model="newName"
          placeholder="Название подборки"
          class="h-11 flex-1 rounded-xl border border-primary px-4 text-sm text-txmain outline-none placeholder:text-txopacity"
          @keyup.enter="create"
        />
        <button
          type="button"
          class="flex size-11 shrink-0 items-center justify-center rounded-xl border border-[#D9E5FF] text-[#2DCD80] disabled:opacity-40"
          :disabled="!newName.trim()"
          @click="create"
        >
          <Icon name="lucide:check" size="22" class="text-[#2DCD80]" />
        </button>
        <button
          type="button"
          class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-bgnew text-txprimary/60"
          @click="cancelCreate"
        >
          <Icon name="lucide:x" size="22" class="text-txprimary/60" />
        </button>
      </div>

      <button
        v-else
        type="button"
        class="mt-4 flex items-center gap-2 text-sm font-medium text-primary"
        @click="isCreating = true"
      >
        <Icon name="lucide:plus" size="18" class="text-primary" />
        Новая подборка
      </button>
    </div>
  </EmptyModal>
</template>

<script setup lang="ts">
  import { ElMessage } from "element-plus";
  import CollectionService, {
    placeKey,
    type Collection,
    type CollectionPlaceRef,
  } from "~/services/CollectionService";
  import EmptyModal from "~/components/publisher/modals/emptyModal.vue";

  const props = defineProps<{ open: boolean; places: CollectionPlaceRef[] }>();
  const emit = defineEmits<{ "update:open": [value: boolean]; saved: [] }>();

  const collections = ref<Collection[]>([]);
  const isLoading = ref(false);
  const isCreating = ref(false);
  const newName = ref("");
  const savingId = ref<number | null>(null);
  const savedIds = ref<Set<number>>(new Set());

  const markSaved = () => {
    const targets = new Set(
      props.places.map((item) => placeKey({ id: item.placeId, social: item.social }))
    );
    const ids = new Set<number>();
    collections.value.forEach((collection) => {
      const inside = (collection.placeCollections ?? []).some((item) =>
        targets.has(placeKey(item.place))
      );
      if (inside) ids.add(collection.id);
    });
    savedIds.value = ids;
  };

  const loadSaved = async () => {
    // Список может отдавать подборки без вложенных каналов — тогда статусы берём из деталей
    const needsDetails = collections.value.some(
      (collection) => (collection.placeCount ?? 0) > 0 && !collection.placeCollections?.length
    );
    if (needsDetails) {
      const details = await Promise.all(
        collections.value.map((collection) =>
          CollectionService.getCollection(collection.id).catch(() => null)
        )
      );
      collections.value = collections.value.map((collection, index) => details[index] ?? collection);
    }
    markSaved();
  };

  const load = async () => {
    isLoading.value = true;
    try {
      const response = await CollectionService.getCollections();
      collections.value = response?.records ?? [];
      await loadSaved();
    } catch (error) {
      console.error("Не удалось загрузить подборки:", error);
      collections.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (collection: Collection) => {
    if (savedIds.value.has(collection.id)) return;
    savingId.value = collection.id;
    try {
      await CollectionService.addPlaces(collection.id, props.places);
      savedIds.value = new Set(savedIds.value).add(collection.id);
      emit("saved");
    } catch (error: any) {
      // 404 — канал пропал из каталога, и бэк не добавил ни одного из выбранных
      ElMessage.error(
        error?.response?.status === 404
          ? "Канал больше не доступен в каталоге — ничего не сохранено"
          : "Не удалось сохранить в подборку"
      );
    } finally {
      savingId.value = null;
    }
  };

  const create = async () => {
    const name = newName.value.trim();
    if (!name) return;
    try {
      const created = await CollectionService.createCollection({ name });
      collections.value = [created, ...collections.value];
      isCreating.value = false;
      newName.value = "";
    } catch (error) {
      ElMessage.error("Не удалось создать подборку");
    }
  };

  const cancelCreate = () => {
    isCreating.value = false;
    newName.value = "";
  };

  watch(
    () => props.open,
    (open) => {
      if (!open) return;
      savedIds.value = new Set();
      cancelCreate();
      load();
    }
  );
</script>

<style scoped>
  .collections-list::-webkit-scrollbar {
    width: 4px;
  }

  .collections-list::-webkit-scrollbar-thumb {
    background-color: theme("colors.primary");
    border-radius: 2px;
  }

  .collections-list::-webkit-scrollbar-track {
    background: transparent;
  }
</style>
